import React, {memo, useState, useEffect, useMemo} from 'react';
import {Box, Button, MenuItem, Select, styled, Typography} from '@mui/material';
import PropTypes from 'prop-types';
import {useGetFSLIOptionsQuery} from '../../../../data/api';
import {renderAmount} from '../../../../util';
import {Modal} from '../../../../components/Modal';

const StyledLabel = styled(Typography)(({theme}) => ({
  color: '#162523',
  fontWeight: 500,
  fontSize: 14,
  marginBottom: theme.spacing(1),
}));

const StyledAccountInfo = styled(Box)(({theme}) => ({
  display: 'flex',
  gap: theme.spacing(2),
  flexDirection: 'row',
  justifyContent: 'space-between',
  borderTop: '1px solid #DFE6E3',
  borderBottom: '1px solid #DFE6E3',
  backgroundColor: '#F2F5F4',
  padding: `${theme.spacing(1.5)} ${theme.spacing(3)}`,
}));

const StyledBody = styled(Box)(({theme}) => ({
  padding: theme.spacing(3),
}));

const StyledReadonlyBox = styled(Box)(({theme}) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(1.25),
  paddingBottom: theme.spacing(1.25),
  borderRadius: 8,
  color: '#162523',
  backgroundColor: '#F0F3F2',
  marginBottom: theme.spacing(3),
}));

const StyledFooter = styled(Box)(({theme}) => ({
  padding: `${theme.spacing(1.5)} ${theme.spacing(3)}`,
  display: 'flex',
  justifyContent: 'end',
  gap: theme.spacing(2),
  borderTop: '1px solid #DFE6E3',
}));

const CancelButton = styled(Button)(() => ({
  color: '#032321',
}));

function ReclassifyModalComp({
  open,
  onClose,
  documentNumber,
  signedAccount,
  indirectPliId,
  amount,
  validIndirectPliIds,
  onReclassify,
}) {
  const [newClassificationId, setNewClassificationId] = useState('');
  const {data, isFetching} = useGetFSLIOptionsQuery({
    pliType: 'INDIRECT_CASHFLOW',
  });

  /** Reset the dropdown when it is closed */
  useEffect(() => {
    if (!open) {
      setNewClassificationId('');
    }
  }, [open]);

  const [options, currentClassification] = useMemo(() => {
    const options = [];
    let currentClassification = null;

    data?.items.forEach((item) => {
      if (item.id === indirectPliId) {
        currentClassification = item;
        options.push(item);
      } else if (validIndirectPliIds.includes(item.id)) {
        options.push(item);
      }
    });

    return [options, currentClassification];
  }, [data?.items, validIndirectPliIds]);

  if (!documentNumber || !signedAccount || !indirectPliId) {
    return null;
  }

  return (
    <Modal open={open} onClose={onClose} title="Reclassify Account">
      <StyledAccountInfo>
        <Box>
          <Typography sx={{color: '#67716E', fontWeight: 500, fontSize: 12}}>
            JE{documentNumber}
          </Typography>
          <Typography sx={{color: '#162523', fontSize: 14}}>
            {signedAccount}
          </Typography>
        </Box>
        <Box sx={{display: 'flex', alignItems: 'end'}}>
          <Typography sx={{color: '#162523', fontWeight: 'bold', fontSize: 14}}>
            {renderAmount(amount)}
          </Typography>
        </Box>
      </StyledAccountInfo>
      <StyledBody>
        <StyledLabel>Current SCF classification</StyledLabel>
        <StyledReadonlyBox>
          {currentClassification?.name || '-'}
        </StyledReadonlyBox>
        <StyledLabel>New SCF classification</StyledLabel>
        <Select
          sx={{mb: 3}}
          fullWidth
          disabled={isFetching}
          value={newClassificationId}
          onChange={({target}) => {
            setNewClassificationId(target.value);
          }}
        >
          {!options.length && (
            <MenuItem disabled>No SCF Options Available</MenuItem>
          )}
          {options.map(({id, name}) => (
            <MenuItem key={id} value={id} disabled={id === indirectPliId}>
              {name}
            </MenuItem>
          ))}
        </Select>
        <Typography sx={{color: '#67716E', fontSize: 12}}>
          The new account classification will be applied to all transactions
          with this account line for statement of cash flow reporting.
        </Typography>
      </StyledBody>
      <StyledFooter>
        <CancelButton variant="text" onClick={onClose}>
          Cancel
        </CancelButton>
        <Button
          variant="contained"
          color="green"
          disabled={!newClassificationId}
          onClick={() => {
            const name = options.find(
              (d) => d.id === newClassificationId
            )?.name;
            onReclassify(newClassificationId, name);
          }}
        >
          Reclassify Account
        </Button>
      </StyledFooter>
    </Modal>
  );
}

ReclassifyModalComp.propTypes = {
  onClose: PropTypes.func.isRequired,
  onReclassify: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  documentNumber: PropTypes.string,
  signedAccount: PropTypes.string,
  indirectPliId: PropTypes.string,
  amount: PropTypes.number,
  validIndirectPliIds: PropTypes.arrayOf(PropTypes.string.isRequired)
    .isRequired,
};

export const ReclassifyModal = memo(ReclassifyModalComp);
